/**
 * App Layout - Main layout for authenticated users
 *
 * Mobile-first layout with bottom navigation bar,
 * header with user info, and main content area.
 */

import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useAuthStore, useHeaderStore } from '../../stores';
import clsx from 'clsx';
import { MediaUploadTeller } from '../MediaUploadTeller';
import { isWerkbank } from '../../lib/uiFlag';

/** Werkbank-shell (plan 2026-09-02) of legacy; per toestel, zie lib/uiFlag. */
const WB = isWerkbank();

// Icons (using simple SVG inline for now)
const HomeIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const RouteIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const navItems = [
  { to: '/', icon: HomeIcon, label: 'Dashboard' },
  { to: '/routes', icon: RouteIcon, label: 'Routes' },
  { to: '/settings', icon: SettingsIcon, label: 'Instellingen' },
];

export function AppLayout() {
  const { user } = useAuthStore();
  const location = useLocation();
  const headerTitle = useHeaderStore((s) => s.title);
  const headerSubtitle = useHeaderStore((s) => s.subtitle);

  // Get page title based on current route
  const getPageTitle = () => {
    if (location.pathname === '/') return 'Dashboard';
    if (location.pathname.startsWith('/routes')) return 'Routes';
    if (location.pathname.startsWith('/workorder')) return 'Werk Order';
    if (location.pathname.startsWith('/inpakken')) return 'Inpakken';
    if (location.pathname === '/settings') return 'Instellingen';
    if (location.pathname.startsWith('/voorinspectie')) return 'Voorinspectie';
    return 'Kleda Field';
  };

  // Werkbank-shell: kop teal-deep met contexttitel uit de headerStore
  // ("Haarlem · 3/14"), nav op de set, content-padding onder voor de nav.
  if (WB) {
    return (
      <div className="min-h-screen wb-body flex flex-col">
        <header className="bg-wb-kop text-wb-kopink safe-area-top sticky top-0 z-40">
          <div className="max-w-container mx-auto h-[56px] px-3 flex items-center justify-between gap-2">
            <div className="min-w-0 flex items-center gap-3">
              <Link to="/" aria-label="Naar het dashboard" className="wb-focus shrink-0 -my-2 py-2 -ml-1 pl-1 pr-1 rounded-wb flex items-center">
                <img src="/kleda-logo-wit.svg" alt="Kleda" className="h-6 w-auto" />
              </Link>
              <h1 className="font-wb-heading font-bold text-lg leading-tight truncate text-wb-kopink">{headerTitle ?? getPageTitle()}</h1>
              {headerSubtitle && <span className="font-mono text-sm font-semibold text-wb-kopink2 shrink-0 self-end pb-0.5">{headerSubtitle}</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <MediaUploadTeller />
              {user && <span className="text-sm text-wb-kopink2 truncate max-w-[120px]">{user.name?.split(' ')[0]}</span>}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto pb-[88px]">
          <div className="max-w-container mx-auto p-3">
            <Outlet />
          </div>
        </main>

        <nav className="fixed bottom-0 left-0 right-0 bg-wb-kop safe-area-bottom z-40 border-t-wb border-wb-mintink">
          <div className="max-w-container mx-auto h-[64px] flex items-stretch">
            {navItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  clsx(
                    'wb-focus flex-1 flex flex-col items-center justify-center gap-0.5 select-none',
                    isActive ? 'text-wb-mint' : 'text-wb-kopink2'
                  )
                }
              >
                <Icon />
                <span className="text-[11px] font-bold uppercase tracking-wide">{label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kleda-bg-clean flex flex-col">
      {/* Header */}
      <header className="bg-kleda-primary text-white py-md px-lg safe-area-top sticky top-0 z-40 shadow-kleda-md">
        <div className="max-w-container mx-auto flex items-center justify-between">
          <h1 className="font-heading text-lg font-bold">{getPageTitle()}</h1>

          <div className="flex items-center gap-sm">
            {/* Staat er nog media op dit toestel, dan moet dat zichtbaar zijn
                op elke pagina — niet alleen op het scherm waar geschoten is. */}
            <MediaUploadTeller />

            {user && (
              <>
                <span className="text-sm opacity-90">{user.name}</span>
                {user.photos?.thumbnail && (
                  <img
                    src={user.photos.thumbnail}
                    alt={user.name}
                    className="w-8 h-8 rounded-full border-2 border-white/30"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main content - with padding for bottom nav */}
      <main className="flex-1 overflow-auto pb-20">
        <div className="max-w-container mx-auto p-lg">
          <Outlet />
        </div>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 safe-area-bottom z-40">
        <div className="max-w-container mx-auto flex justify-around items-center py-sm">
          {navItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                clsx(
                  'flex flex-col items-center gap-xs px-lg py-xs rounded-kleda-sm transition-colors',
                  isActive
                    ? 'text-kleda-primary'
                    : 'text-kleda-text-muted hover:text-kleda-primary'
                )
              }
            >
              <Icon />
              <span className="text-xs font-medium">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
